import {
  createContext,
  useContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAsyncRetry } from 'react-use'
import type { CrmLeadCardInFunnelStatusHistoryItem } from '@/api/types/crm/crmFunnels.types'
import { LeadStatus, Status } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { fetchLeadCardInFunnelDataDeduped } from '../../leadModalRefetchMerge'

interface LeadStatusContextType {
  statuses: LeadStatus[]
  loading: boolean
  refreshStatuses: () => void
}

const LeadStatusContext = createContext<LeadStatusContextType | undefined>(
  undefined,
)

interface LeadStatusProviderProps {
  leadId: number
  funnelId: number | null
  children: ReactNode
  onNeedUpdateLead?: () => void
}

export const LeadStatusProvider: FC<LeadStatusProviderProps> = ({
  leadId,
  funnelId,
  children,
  onNeedUpdateLead,
}) => {
  const addAlert = useAddAlert()
  const {
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    multipleDealsEnabled,
    selectedLeadFunnelId,
  } = useLeadRefetch()
  const normalizedFunnelId =
    funnelId !== null && Number.isFinite(Number(funnelId)) && Number(funnelId) > 0
      ? Number(funnelId)
      : null

  const leadFunnelIdForFetch =
    multipleDealsEnabled &&
    selectedLeadFunnelId != null &&
    Number.isFinite(selectedLeadFunnelId) &&
    selectedLeadFunnelId > 0
      ? selectedLeadFunnelId
      : undefined

  const mapStatusHistoryToLeadStatus = (
    item: CrmLeadCardInFunnelStatusHistoryItem,
  ): LeadStatus => {
    const fallbackStatus: Status = {
      id: item.status_id,
      name: item.status_name,
      color: item.status_color,
      unique_name: '',
      is_auto_new: false,
      is_auto_processed: false,
      is_auto_after_purchase: false,
      is_bad_lead: false,
      is_protected: false,
      is_client_auto_new: false,
      sorting: 0,
    }

    return {
      id: item.id,
      created_at: item.created_at,
      status: fallbackStatus,
    }
  }

  const [useSnapshotMode, setUseSnapshotMode] = useState(true)

  useEffect(() => {
    setUseSnapshotMode(true)
  }, [
    cardFunnelSnapshotEpoch,
    normalizedFunnelId,
    leadId,
    leadFunnelIdForFetch,
  ])

  const snapshotStatuses = useMemo(() => {
    if (!normalizedFunnelId) return undefined as LeadStatus[] | undefined
    const snap = getCardInFunnelSnapshot(
      normalizedFunnelId,
      leadFunnelIdForFetch,
    )
    if (!snap) return undefined
    const history = snap.status_history
    if (!Array.isArray(history)) return undefined
    return history.map(mapStatusHistoryToLeadStatus)
  }, [
    normalizedFunnelId,
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    leadFunnelIdForFetch,
  ])

  const state = useAsyncRetry(async () => {
    if (!normalizedFunnelId) {
      return [] as LeadStatus[]
    }

    const snap = getCardInFunnelSnapshot(
      normalizedFunnelId,
      leadFunnelIdForFetch,
    )
    if (snap && Array.isArray(snap.status_history)) {
      return snap.status_history.map(mapStatusHistoryToLeadStatus)
    }

    const payload = await fetchLeadCardInFunnelDataDeduped(
      leadId,
      normalizedFunnelId,
      leadFunnelIdForFetch,
    )

    if (payload) {
      const stampedLeadFunnelId =
        Number(payload.lead_funnel_id) > 0
          ? Number(payload.lead_funnel_id)
          : leadFunnelIdForFetch
      putCardInFunnelSnapshot(normalizedFunnelId, {
        ...payload,
        ...(stampedLeadFunnelId != null
          ? { lead_funnel_id: stampedLeadFunnelId }
          : {}),
      })
    }

    const statusHistory = payload?.status_history ?? []
    return statusHistory.map(mapStatusHistoryToLeadStatus)
  }, [
    leadId,
    normalizedFunnelId,
    leadFunnelIdForFetch,
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
  ])

  const statuses = useMemo(() => {
    if (snapshotStatuses !== undefined) return snapshotStatuses

    // Сделки ON: не показываем историю предыдущей сделки, пока грузится своя.
    if (leadFunnelIdForFetch != null && state.loading) {
      return [] as LeadStatus[]
    }

    return state.value ?? []
  }, [snapshotStatuses, leadFunnelIdForFetch, state.loading, state.value])

  const loading =
    normalizedFunnelId === null
      ? false
      : useSnapshotMode
        ? snapshotStatuses === undefined && state.loading
        : state.loading

  useEffect(() => {
    if (state.error) {
      const backendError = (state.error as any)?.response?.data?.error
      const errorText = Array.isArray(backendError)
        ? backendError.join(' ')
        : String(backendError ?? '')
      const isLeadFunnelNotFound =
        errorText.toLowerCase().includes('lead funnel not found') ||
        errorText.toLowerCase().includes('воронка не найдена')

      if (isLeadFunnelNotFound) return

      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить статусы заявки',
        type: 'error',
      })
    }
  }, [state.error, addAlert])

  const handlerRefetchStatuses = () => {
    setUseSnapshotMode(false)
    state.retry()
    onNeedUpdateLead && onNeedUpdateLead()
  }

  return (
    <LeadStatusContext.Provider
      value={{
        statuses,
        loading,
        refreshStatuses: handlerRefetchStatuses,
      }}
    >
      {children}
    </LeadStatusContext.Provider>
  )
}

export const useLeadStatuses = () => {
  const context = useContext(LeadStatusContext)
  if (!context) {
    throw new Error('useLeadStatuses must be used within a LeadStatusProvider')
  }
  return context
}
